import { Box, Flex, Text } from "@chakra-ui/react";
import React, { ReactNode, useMemo } from "react";
import { FaCheck } from "react-icons/fa";

export type CheckboxOption = {
  label: string;
  uuid: string;
  checked: boolean;
  color?: string;
  category?: string;
};

interface Props {
  type: string;
  checkboxOptions: CheckboxOption[];
  filterFunction: (ids: string[]) => void;
  handleUnlinkedClick?: (add: boolean) => void;
  unlinkedChecked?: boolean;
  children?: ReactNode;
}

type CheckboxSelfProps = {
  checked: boolean;
  handleClick: (add: boolean, id?: string) => void;
  children: string;
  uuid?: string;
  color?: string;
};

const CheckboxSelf: React.FC<CheckboxSelfProps> = ({
  checked,
  uuid,
  color,
  handleClick,
  children,
}) => (
  <Flex
    cursor="pointer"
    alignItems="center"
    overflow="hidden"
    my="1"
    onClick={() => handleClick(!checked, uuid)}
  >
    <Flex
      w="5"
      h="5"
      mr="3"
      borderRadius="3"
      border="grey 1.5px solid"
      justifyContent="center"
      alignItems="center"
      flexShrink={0}
      bg={checked ? color || "grey" : "white"}
    >
      <FaCheck size="14" color="white" />
    </Flex>
    <Text title={children} whiteSpace="nowrap" overflow="hidden" w="full" textOverflow="ellipsis">
      {children}
    </Text>
  </Flex>
);

const MultiCheckboxes: React.FC<Props> = (props) => {
  const { type, checkboxOptions, filterFunction, handleUnlinkedClick, unlinkedChecked } = props;
  const checkedArray = checkboxOptions.filter(({ checked }) => checked);
  const allChecked =
    checkedArray.length === checkboxOptions.length &&
    (unlinkedChecked === undefined || unlinkedChecked);

  const handleAllClick = (add: boolean) => {
    if (add) filterFunction(checkboxOptions.map((checkboxOption) => checkboxOption.uuid));
    if (!add) filterFunction([]);
    if (handleUnlinkedClick) handleUnlinkedClick(add);
  };

  const handleClick = (add: boolean, id?: string) => {
    if (!id) return;
    const newIds: string[] = [];
    checkboxOptions.forEach(({ checked, uuid }) => {
      if (!add && uuid === id) return;
      if (checked || (add && uuid === id)) newIds.push(uuid);
    });
    filterFunction(newIds);
  };

  const visualizeCheckboxOptions = useMemo(() => {
    // Map the organisations to an object to quickly get them in the gmn map
    const categoryNameMap: { [category: string]: CheckboxOption[] } = {};
    checkboxOptions.forEach((option) => {
      if (!categoryNameMap[option.category || ""]) categoryNameMap[option.category || ""] = [];
      categoryNameMap[option.category || ""].push(option);
    });
    return categoryNameMap;
  }, [checkboxOptions]);

  return (
    <>
      <CheckboxSelf checked={allChecked} handleClick={handleAllClick}>
        {allChecked ? "Allemaal" : `${type}'s: ${checkedArray.length}`}
      </CheckboxSelf>
      {handleUnlinkedClick && unlinkedChecked !== undefined && (
        <CheckboxSelf color="#ff8c00" checked={unlinkedChecked} handleClick={handleUnlinkedClick}>
          {"Zonder " + type}
        </CheckboxSelf>
      )}

      {Object.entries(visualizeCheckboxOptions).map(([category, options], idx) => {
        return (
          <Box key={category + idx}>
            {category && (
              <Text fontWeight="bold" mt={4} mb="2">
                {category}:
              </Text>
            )}

            {options.map(({ label, uuid, checked, color }) => (
              <CheckboxSelf
                key={uuid}
                checked={checked}
                color={color}
                uuid={uuid}
                handleClick={handleClick}
              >
                {label}
              </CheckboxSelf>
            ))}
          </Box>
        );
      })}
    </>
  );
};

export { MultiCheckboxes };
